import * as React from 'react';

// =============================================================================
// template-preview.tsx — Prévisualisation d'un template de messagerie (écran A-24).
// =============================================================================
// DBU §2.8 l.1926 : « Possibilité de prévisualisation des templates avant
// enregistrement. » Ce composant rend le contenu du template AVANT le PATCH.
//
// SÉCURITÉ CRITIQUE (SECOPS) — INERTIE XSS : le contenu est admin-authored et peut
// contenir des séquences ressemblant à du HTML (`<script>`, `<img onerror=…>`). Il
// est rendu STRICTEMENT COMME TEXTE via l'échappement natif de React (`{content}`).
// Il est INTERDIT d'utiliser `dangerouslySetInnerHTML` ici : le navigateur ne doit
// JAMAIS parser ni exécuter ce contenu. Un `<pre className="whitespace-pre-wrap">`
// préserve les retours à la ligne et les espaces significatifs du template.
//
// Preuve d'inertie (DoD R3-A) : pour un contenu `<script>alert(1)</script>`, le DOM
// produit ne contient AUCUN nœud <script>/<img> (querySelector === null) et le
// textContent porte le littéral `<script>alert(1)</script>`.

export interface TemplatePreviewProps {
  /** Contenu brut du template (valeur en cours d'édition, avant enregistrement). */
  readonly content: string;
}

/**
 * Panneau de prévisualisation inerte. Aucune interprétation HTML : le contenu est
 * du texte, un point c'est tout. Rendu réactif à la saisie (le parent passe la
 * valeur courante du champ d'édition).
 */
export function TemplatePreview({
  content,
}: TemplatePreviewProps): React.JSX.Element {
  return (
    <div
      role="region"
      aria-label="Prévisualisation du template"
      className="space-y-2 rounded-md border border-border bg-muted/30 p-4"
    >
      <p className="text-xs font-medium text-muted-foreground">
        Prévisualisation (avant enregistrement)
      </p>
      {content.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">
          Le template est vide.
        </p>
      ) : (
        // Échappement NATIF React : `{content}` est rendu comme TEXTE inerte.
        // JAMAIS `dangerouslySetInnerHTML` (contenu admin-authored, garde XSS).
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
          {content}
        </pre>
      )}
    </div>
  );
}
